import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BedDouble, Building2, Lock, Plus, Sparkles, Store, UtensilsCrossed, Wallet, CalendarHeart, CircleEllipsis } from 'lucide-react';
import { DashboardHeader } from '../Dashboard/DashboardUi';
import { Modal } from '../ui/Modal';
import { Field, Input, Select } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import { useAuth } from '../../context/AuthContext';
import { LIBELLES_TYPE_SERVICE, messageErreur, organisationApi, type ArbreOrganisation, type ServiceFiche, type TypeService } from '../../lib/organisationApi';
import { EtatEchec, Pastille, Squelettes } from './EtatsPage';
import { useOrganisation } from './OrganisationContext';

/** Vue d'ensemble de l'organisation (maquette L01-02) : organisation → établissements → services. */

export const ICONES_SERVICE: Record<TypeService, React.ComponentType<{ size?: number }>> = {
    hotel: BedDouble, restaurant: UtensilsCrossed, spa: Sparkles, prestations: Store, evenementiel: CalendarHeart, autre: CircleEllipsis,
};

const formaterSiret = (s: string) => (s.length === 14 ? `${s.slice(0, 3)} ${s.slice(3, 6)} ${s.slice(6, 9)} ${s.slice(9)}` : s);

const Ligne: React.FC<{ marge: number; icone: React.ReactNode; nom: string; sous: string; societe?: React.ReactNode; modules: React.ReactNode; personnes: React.ReactNode; onOuvrir?: () => void }> = ({ marge, icone, nom, sous, societe, modules, personnes, onOuvrir }) => (
    <div
        role={onOuvrir ? 'button' : undefined}
        tabIndex={onOuvrir ? 0 : undefined}
        onClick={onOuvrir}
        onKeyDown={onOuvrir ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOuvrir(); } } : undefined}
        className={`grid grid-cols-[minmax(0,1fr)_64px_72px] md:grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)_90px_90px] items-center gap-3 px-4 min-h-[60px] py-2 border-b border-border-soft last:border-b-0 ${onOuvrir ? 'cursor-pointer hover:bg-surface-soft' : ''}`}
    >
        <div className="flex items-center gap-3 min-w-0" style={{ paddingLeft: marge }}>
            <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--acc-bg)', color: 'var(--acc-fg)' }}>{icone}</span>
            <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-content truncate">{nom}</span>
                <span className="block text-[11.5px] text-content-muted truncate">{sous}</span>
            </span>
        </div>
        <div className="hidden md:block min-w-0 text-[12.5px]">{societe}</div>
        <div className="text-[13px] font-semibold text-content tabular-nums">{modules}</div>
        <div className="text-[13px] font-semibold text-content tabular-nums">{personnes}</div>
    </div>
);

export const NouveauServiceModal: React.FC<{ open: boolean; onClose: () => void; onCree: (s: ServiceFiche) => void; clientId?: string }> = ({ open, onClose, onCree, clientId }) => {
    const [nom, setNom] = useState('');
    const [type, setType] = useState<TypeService>('restaurant');
    const [erreur, setErreur] = useState('');
    const [envoi, setEnvoi] = useState(false);
    useEffect(() => { if (open) { setNom(''); setType('restaurant'); setErreur(''); } }, [open]);
    const valider = async () => {
        if (!nom.trim()) { setErreur('Le nom du service est obligatoire.'); return; }
        setEnvoi(true);
        try { onCree(await organisationApi.creerService({ name: nom.trim(), type }, clientId)); onClose(); }
        catch (e) { setErreur(messageErreur(e)); }
        finally { setEnvoi(false); }
    };
    return (
        <Modal
            open={open} onClose={onClose} size="md" title="Ajouter un service" subtitle="Une activité de plus dans cet établissement : société, SIRET et TVA se règlent ensuite sur sa fiche."
            footer={<><button type="button" className={pillSecondary(36)} onClick={onClose}>Annuler</button><button type="button" className={pillPrimary(36)} disabled={envoi} onClick={valider}>Ajouter le service</button></>}
        >
            <div className="space-y-4">
                <Field label="Nom du service" required error={erreur}>{(f) => <Input {...f} value={nom} onChange={(e) => setNom(e.target.value)} maxLength={80} autoFocus />}</Field>
                <Field label="Type">{(f) => (
                    <Select {...f} value={type} onChange={(e) => setType(e.target.value as TypeService)}>
                        {(Object.keys(LIBELLES_TYPE_SERVICE) as TypeService[]).map((t) => <option key={t} value={t}>{LIBELLES_TYPE_SERVICE[t]}</option>)}
                    </Select>
                )}</Field>
            </div>
        </Modal>
    );
};

export const OrganisationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { recharger } = useOrganisation();
    const [arbre, setArbre] = useState<ArbreOrganisation | null>(null);
    const [erreur, setErreur] = useState<unknown>(null);
    const [modal, setModal] = useState(false);

    const charger = useCallback(async () => {
        setErreur(null);
        try { setArbre(await organisationApi.arbre()); } catch (e) { setErreur(e); }
    }, []);
    useEffect(() => { void charger(); }, [charger]);

    return (
        <div className="space-y-4 max-w-[1100px]">
            <DashboardHeader
                societe={user?.company_name || ''}
                titre="Mon établissement"
                sousTitre="Vos informations et vos services."
                actions={<><button type="button" className={`${pillSecondary(34)} max-md:!h-11`} onClick={() => navigate('/organisation/poles-groupe')}>Pôles et groupe</button><button type="button" className={`${pillPrimary(34)} max-md:!h-11`} onClick={() => setModal(true)} disabled={!arbre}><Plus size={14} /> Ajouter un service</button></>}
            />
            {erreur ? <EtatEchec erreur={erreur} titre="Impossible d'afficher votre établissement" onRetry={charger} /> : !arbre ? <Squelettes lignes={4} /> : arbre.etablissements.length === 1 ? (
                <>
                    <section aria-label="Informations de l'établissement" className="bg-surface border border-border rounded-xl p-4 flex flex-wrap items-center gap-4">
                        <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--acc-bg)', color: 'var(--acc-fg)' }}><Building2 size={20} /></span>
                        <div className="min-w-0 flex-1 basis-[220px]">
                            <h2 className="text-[16px] font-bold text-content tracking-tight truncate">{arbre.etablissements[0].name}</h2>
                            <p className="text-[12.5px] text-content-muted truncate">{[arbre.etablissements[0].adresse, arbre.etablissements[0].ville].filter(Boolean).join(', ') || 'Adresse non renseignée'}</p>
                        </div>
                        <button type="button" className={`${pillSecondary(34)} max-md:!h-11`} onClick={() => navigate('/organisation/etablissement')}>Modifier les informations</button>
                    </section>
                    <section aria-label="Services">
                        <h2 className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-2 px-0.5">Mes services</h2>
                        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                            {arbre.etablissements[0].services.map((s) => {
                                const Icone = ICONES_SERVICE[s.type] || CircleEllipsis;
                                return (
                                    <button key={s.id} type="button" onClick={() => navigate(`/organisation/services/${s.id}`)} className="text-left bg-surface border border-border rounded-xl p-4 hover:border-content transition-colors min-h-[44px]">
                                        <span className="flex items-center gap-3">
                                            <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--acc-bg)', color: 'var(--acc-fg)' }}><Icone size={16} /></span>
                                            <span className="min-w-0"><span className="block text-[14px] font-semibold text-content truncate">{s.name}</span><span className="block text-[12px] text-content-muted truncate">{s.societe || LIBELLES_TYPE_SERVICE[s.type]}</span></span>
                                        </span>
                                        <span className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-content-muted">
                                            <span><b className="text-content tabular-nums">{s.modules_actifs}</b> module{s.modules_actifs > 1 ? 's' : ''} activé{s.modules_actifs > 1 ? 's' : ''}</span>
                                            {s.responsable_nom && <span>Responsable : {s.responsable_nom}</span>}
                                            {s.autre_societe && <Pastille ton="warn" icone={<Lock size={11} />}>Données non partagées</Pastille>}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                    <p className="text-[12px] text-content-muted px-1">Un service est une activité de votre établissement (restaurant, hôtel, spa…). Chacun peut avoir sa société, son SIRET et ses modules.</p>
                </>
            ) : (
                <>
                    <div className="bg-surface border border-border rounded-xl overflow-hidden">
                        <div className="hidden md:grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)_90px_90px] gap-3 px-4 h-10 items-center border-b border-border bg-surface-soft text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">
                            <span>Nom</span><span>Société · SIRET</span><span>Modules</span><span>Personnes</span>
                        </div>
                        <Ligne marge={0} icone={<Wallet size={16} />} nom={arbre.organisation.name} sous="Organisation · compte payeur" societe="—" modules={arbre.organisation.modules_transverses_actifs} personnes={arbre.etablissements.reduce((n, e) => n + e.personnes, 0)} />
                        {arbre.etablissements.map((e) => (
                            <React.Fragment key={e.id}>
                                <Ligne marge={24} icone={<Building2 size={16} />} nom={e.name} sous={`Établissement${e.ville ? ` · ${e.ville}` : ''} · ${e.services.length} service${e.services.length > 1 ? 's' : ''}`} societe="—" modules="—" personnes={e.personnes} onOuvrir={() => navigate('/organisation/etablissement')} />
                                {e.services.map((s) => {
                                    const Icone = ICONES_SERVICE[s.type] || CircleEllipsis;
                                    return (
                                        <Ligne
                                            key={s.id} marge={48} icone={<Icone size={16} />} nom={s.name}
                                            sous={`Service${s.responsable_nom ? ` · responsable ${s.responsable_nom}` : ''}`}
                                            societe={(
                                                <>
                                                    <span className="block font-semibold text-content truncate">{s.societe || '—'}</span>
                                                    {s.siret && <span className="block font-mono text-[11.5px] text-content-muted">{formaterSiret(s.siret)}</span>}
                                                    {s.autre_societe && <span className="mt-1 inline-block"><Pastille ton="warn" icone={<Lock size={11} />}>Données non partagées</Pastille></span>}
                                                </>
                                            )}
                                            modules={s.modules_actifs} personnes="—" onOuvrir={() => navigate(`/organisation/services/${s.id}`)}
                                        />
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </div>
                    <p className="text-[12px] text-content-muted px-1">Les modules « transverses » (Clients, Facture) appartiennent à l'organisation ; les autres sont rattachés à un service.</p>
                </>
            )}
            <NouveauServiceModal open={modal} onClose={() => setModal(false)} onCree={(s) => { void recharger(); navigate(`/organisation/services/${s.id}`); }} />
        </div>
    );
};

export default OrganisationPage;
